/**
 * String-safe Decimal display helpers (HON-03).
 *
 * The backend serialises every monetary / quantity DecimalField to a JSON
 * STRING (DRF default for DecimalField). Re-deriving an authoritative figure
 * through `parseFloat` / `Number()` reintroduces IEEE-754 drift — the v1.1
 * float-drift class of bug. Per the Phase-11 UI-SPEC "Design System" note,
 * `utils/format.ts#formatCurrency(value: number)` is therefore UNSAFE for
 * authoritative money on the Analytics tab.
 *
 * These helpers format and sum backend Decimal strings using sign + integer +
 * fraction string parsing and BigInt minor-unit accumulation only. No floating
 * point ever touches an authoritative value. Framework-free (no React).
 */

const EM_DASH = '—';

/**
 * Expand a JS number to a PLAIN decimal string (no exponential), pure string work.
 *
 * `String(1e-7)` is `"1e-7"` and `String(1e21)` is `"1e+21"` — the `parseDecimal`
 * grammar rejects the `e`, so an exponential numeric fallback silently became "—" or was
 * DROPPED from a sum (L5). The backend serialises money as plain strings, so this only
 * bites the defensive number-coercion path; the expansion is exact (digit shifting, no
 * float arithmetic). Non-finite (NaN / Infinity) passes through for parseDecimal to reject.
 */
function numberToPlainString(value: number): string {
  const s = String(value);
  if (!/e/i.test(s)) return s;
  const [mantissa, expPart] = s.split(/e/i);
  const exp = Number(expPart);
  const negative = mantissa.startsWith('-');
  const unsigned = negative ? mantissa.slice(1) : mantissa;
  const [intDigits, fracDigits = ''] = unsigned.split('.');
  const digits = intDigits + fracDigits;
  const pointPos = intDigits.length + exp; // decimal-point index from the left
  let body: string;
  if (pointPos <= 0) {
    body = `0.${'0'.repeat(-pointPos)}${digits}`;
  } else if (pointPos >= digits.length) {
    body = digits + '0'.repeat(pointPos - digits.length);
  } else {
    body = `${digits.slice(0, pointPos)}.${digits.slice(pointPos)}`;
  }
  return negative ? `-${body}` : body;
}

/**
 * Coerce a backend decimal field to a trimmed string, or null when absent/empty.
 *
 * The backend SHOULD serialise every DecimalField to a JSON STRING (DRF
 * default), and the PositionDetail TS types declare these fields as `string`.
 * In practice some figures (rate / price / amount fields) arrive as JS NUMBERS
 * at runtime — calling a string method (`.trim()`, `.startsWith()`) directly on
 * a number throws `value.trim is not a function` and crashes the whole tab.
 *
 * This helper is the single consumption-boundary guard: any decimal field that
 * feeds a raw `.trim()` / `.startsWith()` MUST pass through here first. `String()`
 * stringifies the value the backend already sent — NO parseFloat / arithmetic,
 * so the HON-03 no-float-on-authoritative-values invariant holds. Returns null
 * for null / undefined / empty-after-trim so callers can branch on presence.
 */
export function toDecimalString(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  const str = typeof value === 'number' ? numberToPlainString(value) : value;
  const trimmed = str.trim();
  return trimmed === '' ? null : trimmed;
}

/** Internal scale for accumulation — matches the backend's 8dp accrual precision. */
const SUM_SCALE = 8;

interface ParsedDecimal {
  /** true when the value is strictly negative (before any rounding). */
  negative: boolean;
  /** Digits left of the point, no sign, no separators (may be ""). */
  intPart: string;
  /** Digits right of the point, no sign (may be ""). */
  fracPart: string;
}

/**
 * Parse a Decimal string into sign + integer + fraction. Pure string work —
 * never Number()/parseFloat. Returns null for non-finite / malformed input.
 */
function parseDecimal(value: string): ParsedDecimal | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(trimmed);
  if (!match) return null;

  const sign = match[1];
  const intPart = match[2] ?? '';
  const fracPart = match[3] ?? '';

  // Reject a bare sign / lone dot with no digits at all.
  if (intPart === '' && fracPart === '') return null;

  return {
    negative: sign === '-',
    intPart,
    fracPart,
  };
}

/**
 * Round a fraction string to `dp` places using half-up on magnitude, returning
 * the rounded integer-part (as a digit string, possibly carrying) and the
 * padded/truncated fraction (exactly `dp` chars). All BigInt — no float.
 */
function roundHalfUp(
  intPart: string,
  fracPart: string,
  dp: number,
): { intDigits: string; fracDigits: string } {
  const intDigits = intPart === '' ? '0' : intPart;

  if (dp >= fracPart.length) {
    // No rounding needed — just pad.
    return { intDigits, fracDigits: fracPart.padEnd(dp, '0') };
  }

  const kept = fracPart.slice(0, dp);
  const nextDigit = fracPart.charCodeAt(dp) - 48; // '0' === 48

  // Combine int + kept-fraction into a single BigInt of minor units at `dp`.
  const scaledStr = intDigits + kept; // both are pure digit strings
  let scaled = BigInt(scaledStr === '' ? '0' : scaledStr);

  if (nextDigit >= 5) {
    scaled += 1n;
  }

  // Re-split into integer and fraction at `dp`.
  const scaledDigits = scaled.toString();
  let finalInt: string;
  let finalFrac: string;
  if (dp === 0) {
    finalInt = scaledDigits;
    finalFrac = '';
  } else if (scaledDigits.length <= dp) {
    finalInt = '0';
    finalFrac = scaledDigits.padStart(dp, '0');
  } else {
    finalInt = scaledDigits.slice(0, scaledDigits.length - dp);
    finalFrac = scaledDigits.slice(scaledDigits.length - dp);
  }

  return { intDigits: finalInt, fracDigits: finalFrac };
}

/** Apply thousands grouping to a pure-digit integer string. */
function groupThousands(intDigits: string): string {
  const normalized = intDigits.replace(/^0+(?=\d)/, ''); // strip leading zeros, keep one
  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** True when every digit (int + frac) is zero. */
function isZeroDigits(intDigits: string, fracDigits: string): boolean {
  return /^0*$/.test(intDigits) && /^0*$/.test(fracDigits);
}

/**
 * Structured pieces of a formatted backend Decimal — the same rounding path as
 * `formatDecimalString`, but with the integer body, fraction tail, sign and
 * currency symbol kept SEPARATE so the UI can demote the decimal/unit tail
 * (the `.lr-dec` 0.58em faint span — number-priority typography). Composing the
 * pieces back joined === `formatDecimalString` output (one rounding source).
 */
export interface DecimalParts {
  /** value was null / empty / malformed — caller renders the em dash. */
  isEmpty: boolean;
  /** true when a leading "-" should show (negative AND not rounded-to-zero). */
  negative: boolean;
  /** "" for positive / zero-magnitude, "-" otherwise. */
  sign: '' | '-';
  /** Currency symbol ("$") or "" when no currency requested. */
  currencySymbol: string;
  /** Integer part with thousands grouping, e.g. "154,335". */
  intGrouped: string;
  /** Fraction digits, exactly `dp` chars (e.g. "00"); "" when dp === 0. */
  frac: string;
  /** Display fraction digits actually used. */
  dp: number;
}

/**
 * Decompose a backend Decimal STRING into display pieces (HON-03 — pure
 * string/BigInt, no parseFloat).
 *
 * - null / undefined / empty / malformed -> `{ isEmpty: true, … }`; caller emits "—".
 * - Negative values carry `sign: "-"`; a value that rounds to zero magnitude
 *   has `sign: ""` (no "-$0.00").
 *
 * @param value backend Decimal string (e.g. "12480.55000000"), or a JS number
 *   (some PositionDetail fields arrive as numbers at runtime — coerced via
 *   numberToPlainString, NO float arithmetic, so HON-03 holds).
 * @param opts.currency ISO currency code; when set, exposes the symbol
 * @param opts.dp display fraction digits (default 2 = cent; 8 for tooltips)
 */
export function formatDecimalParts(
  value: string | number | null | undefined,
  opts: { currency?: string; dp?: number } = {},
): DecimalParts {
  const dp = opts.dp ?? 2;
  const currencySym = opts.currency ? currencySymbol(opts.currency) : '';
  const empty: DecimalParts = {
    isEmpty: true,
    negative: false,
    sign: '',
    currencySymbol: currencySym,
    intGrouped: '',
    frac: dp > 0 ? '0'.repeat(dp) : '',
    dp,
  };

  if (value == null) return empty;
  const str = typeof value === 'number' ? numberToPlainString(value) : value;
  if (str.trim() === '') return empty;

  const parsed = parseDecimal(str);
  if (parsed === null) return empty;

  const { intDigits, fracDigits } = roundHalfUp(parsed.intPart, parsed.fracPart, dp);
  const showSign = parsed.negative && !isZeroDigits(intDigits, fracDigits);

  return {
    isEmpty: false,
    negative: showSign,
    sign: showSign ? '-' : '',
    currencySymbol: currencySym,
    intGrouped: groupThousands(intDigits),
    frac: dp > 0 ? fracDigits : '',
    dp,
  };
}

/**
 * Format a backend Decimal STRING for display (the joined form of
 * `formatDecimalParts`).
 *
 * - Grouping + currency symbol are applied to the already-correct backend
 *   string; rounding/padding to `dp` is BigInt/string-based (HON-03).
 * - null / undefined / empty -> em dash ("—"), matching format.ts.
 * - Negative values carry a leading sign; a value that rounds to zero
 *   magnitude renders without a sign (no "-$0.00").
 *
 * @param value backend Decimal string (e.g. "12480.55000000") or null
 * @param opts.currency ISO currency code; when set, prefixes the symbol
 * @param opts.dp display fraction digits (default 2 = cent; 8 for tooltips)
 */
export function formatDecimalString(
  value: string | number | null | undefined,
  opts: { currency?: string; dp?: number } = {},
): string {
  const parts = formatDecimalParts(value, opts);
  if (parts.isEmpty) return EM_DASH;

  const body = parts.dp > 0 ? `${parts.intGrouped}.${parts.frac}` : parts.intGrouped;

  // Sign first, then symbol, then digits: "-$3.20".
  return `${parts.sign}${parts.currencySymbol}${body}`;
}

/**
 * Resolve a currency symbol via Intl without ever passing the authoritative
 * value through a Number — we format a throwaway 0 only to extract the symbol.
 */
function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? `${currency} `;
  } catch {
    return `${currency} `;
  }
}

/**
 * Sum a list of backend Decimal strings WITHOUT float.
 *
 * Each value is normalised to 8dp, converted to signed BigInt minor units,
 * accumulated, then re-rendered as an 8dp Decimal string. This is the
 * hero / cumulative aggregate (ANLY-01); it is cent-equal to a backend
 * Decimal sum. nulls are skipped. Empty input -> "0".
 */
export function sumDecimalStrings(values: Array<string | number | null | undefined>): string {
  let total = 0n;
  let counted = 0;
  const scaleFactor = SUM_SCALE;

  for (const value of values) {
    if (value == null) continue;
    const str = typeof value === 'number' ? numberToPlainString(value) : value;
    if (str.trim() === '') continue;
    const parsed = parseDecimal(str);
    if (parsed === null) continue;

    // Normalise fraction to exactly SUM_SCALE digits, ROUNDING half-up beyond
    // 8dp (consistent with formatDecimalString) so a >8dp input can't silently
    // under-report by truncation (WR-01). Accrual strings are already 8dp so
    // this is a no-op in practice; the rounding only bites on >8dp callers.
    const { intDigits, fracDigits } = roundHalfUp(parsed.intPart, parsed.fracPart, scaleFactor);
    const minor = BigInt(intDigits + fracDigits);
    total += parsed.negative ? -minor : minor;
    counted += 1;
  }

  if (counted === 0) return '0';

  const negative = total < 0n;
  const abs = (negative ? -total : total).toString().padStart(scaleFactor + 1, '0');
  const intDigits = abs.slice(0, abs.length - scaleFactor);
  const fracDigits = abs.slice(abs.length - scaleFactor);
  const sign = negative && !/^0*$/.test(abs) ? '-' : '';

  return `${sign}${intDigits}.${fracDigits}`;
}
